/**
 * Anything `JSON.parse` can hand back.
 *
 * The owner type for the free-form bags the API sends — activity-log
 * `properties`, provider `meta_data`, notification `metadata` — where the keys
 * vary per record and only the reader knows which ones it wants. It says the
 * value is JSON, which is what lets a consumer render it, stringify it, or
 * narrow it; `unknown` says nothing at all.
 */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

/** A free-form JSON object: the bag itself, rather than one value in it. */
export type JsonObject = Record<string, JsonValue>;

/** Whether the JSON value is an object: an array is not one, and neither is null. */
export function isJsonObject(value: JsonValue | undefined): value is JsonObject {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Whether the JSON value is a string. */
export function isJsonString(value: JsonValue | undefined): value is string {
    return typeof value === 'string';
}

/** Whether the JSON value is a number. */
export function isJsonNumber(value: JsonValue | undefined): value is number {
    return typeof value === 'number';
}

/** Whether the JSON value is a boolean. */
export function isJsonBoolean(value: JsonValue | undefined): value is boolean {
    return typeof value === 'boolean';
}

/** The JSON value as an array, or an empty one when it is anything else. */
export function jsonArray(value: JsonValue | undefined): JsonValue[] {
    return Array.isArray(value) ? value : [];
}
